import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalContent,
  ModalOverlay,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import CloseIconBlack from "../../assets/images/booking/x-circle.svg";
import { useReferralEarningDetail } from "../../hooks/useReferralEarning";
import { employeeCall } from "../../api/employee";
import PopupProfileCompleted from "../../components/popup/popupProfileCompleted";
import { formatDateDb } from "../../utils/tools";
import { formatNumber } from "../../utils/utils";

const SOURCE_LABEL = {
  COMPANY: "Nguồn công ty",
  PERSONAL: "Nguồn cá nhân",
};

// Trạng thái mốc, cùng chữ với app
const STATUS_LABEL = {
  ACHIEVED: "Đã đạt",
  IN_PROGRESS: "Chưa đạt",
  MISSED: "Không đạt",
};

const formatDate = (value) => (value ? formatDateDb(value) : "-");

const formatAmount = (value) =>
  `${formatNumber(Math.round(Number(value) || 0))} VNĐ`;

// Nội dung và số tiền của một mốc, giống ReferredEmployeeCard trên app
const milestoneUi = (milestone) => {
  // Mốc tính theo giao dịch không có số công và không có hạn chốt
  const isByTransaction = !milestone.requiredWorkingDay;

  if (milestone.status === "ACHIEVED") {
    const transactionCode = milestone.transaction?.lockingCode;
    return {
      lines: [
        transactionCode
          ? `GD ${transactionCode} · ${formatDate(milestone.achievedDate)}`
          : `Hoàn thành ngày ${formatDate(milestone.achievedDate)}`,
      ],
      color: "var(--color-status-employ)",
      amount: milestone.paidAmount,
    };
  }

  if (milestone.status === "MISSED") {
    return {
      lines: [
        isByTransaction
          ? "Ứng viên đã nghỉ việc"
          : `Hết hạn ngày ${formatDate(milestone.dueDate)}`,
      ],
      color: "var(--text-red-employee)",
      amount: 0,
    };
  }

  return {
    lines: isByTransaction
      ? [
          "Tiêu chí xét duyệt: Ứng viên phát sinh giao dịch đầu tiên trong thời gian làm việc tại công ty",
        ]
      : [
          `Điều kiện hợp lệ: Ứng viên cần hoàn thành tối thiểu ${
            milestone.requiredWorkingDay
          } ngày công trước ngày ${formatDate(milestone.dueDate)}`,
          `(Tiến độ hiện tại: ${milestone.workingDay}/${milestone.requiredWorkingDay} công).`,
        ],
    color: "var(--text-color-Subtittle)",
    amount: milestone.provisionalAmount,
  };
};

// Mỗi người được giới thiệu là 1 mục thu gọn được: bấm phần đầu để xem các mốc.
// Chưa có nguồn thì có nút "Duyệt" (nằm ngoài vùng bấm thu gọn).
const ReferredCard = ({ item, onApprove }) => {
  const paidAmount = (item?.milestones || []).reduce(
    (sum, milestone) => sum + (Number(milestone.paidAmount) || 0),
    0,
  );

  return (
    <AccordionItem
      border={"1px solid var(--Line)"}
      borderRadius={"8px"}
      mb={"12px"}
      overflow={"hidden"}
    >
      <Flex
        alignItems={"center"}
        gap={"12px"}
        pr={item?.referralSource ? 0 : "12px"}
        backgroundColor="var(--fill-avatar)"
      >
        <AccordionButton
          flex={1}
          gap={"16px"}
          p={"12px"}
          textAlign={"start"}
          _hover={{ backgroundColor: "transparent" }}
        >
          <Box flex={1}>
            <Text
              fontSize={"14px"}
              fontWeight={600}
              color="var(--color-header-employee)"
            >
              {item?.fullName || "-"}
            </Text>
            <Text fontSize={"12px"} color="var(--text-color-Subtittle)">
              {[
                item?.code || "-",
                item?.jobPositionName,
                `Vào làm ${formatDate(item?.startTime)}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Text>
          </Box>
          <Box textAlign={"end"}>
            <Text
              fontSize={"12px"}
              fontWeight={600}
              color={
                item?.referralSource
                  ? "var(--color-status-employ)"
                  : "var(--color-label-employee)"
              }
            >
              {SOURCE_LABEL[item?.referralSource] || "Chưa có nguồn"}
            </Text>
            <Text fontSize={"12px"} color="var(--color-info-employee)">
              Tạm tính: {formatAmount(item?.provisionalAmount)} · Đã nhận đến
              nay: {formatAmount(paidAmount)}
            </Text>
          </Box>
          <AccordionIcon color="var(--color-info-employee)" />
        </AccordionButton>
        {!item?.referralSource && (
          <Button
            h={"32px"}
            minW={"72px"}
            borderRadius={"8px"}
            fontSize={"13px"}
            background={"var(--linear-gradient-employee)"}
            color={"var(--color-main)"}
            _hover={{ filter: "brightness(90%)" }}
            _active={{ filter: "brightness(90%)" }}
            onClick={() => onApprove(item)}
          >
            Duyệt
          </Button>
        )}
      </Flex>

      <AccordionPanel p={0}>
        {(item?.milestones || []).map((milestone) => {
          const ui = milestoneUi(milestone);

          return (
            <Flex
              key={milestone.key}
              alignItems={"flex-start"}
              justifyContent={"space-between"}
              gap={"16px"}
              p={"8px 12px"}
              borderTop={"1px solid var(--Line)"}
            >
              <Box flex={1}>
                <Text
                  fontSize={"13px"}
                  fontWeight={600}
                  color="var(--color-info-employee)"
                >
                  {milestone.name} -{" "}
                  <Text
                    as="span"
                    color={
                      milestone.status === "ACHIEVED"
                        ? "var(--color-status-employ)"
                        : "var(--text-red-employee)"
                    }
                  >
                    {STATUS_LABEL[milestone.status]}
                  </Text>
                </Text>
                {ui.lines.map((line, index) => (
                  <Text key={index} fontSize={"12px"} color={ui.color}>
                    {line}
                  </Text>
                ))}
              </Box>
              <Text
                fontSize={"13px"}
                fontWeight={600}
                whiteSpace={"nowrap"}
                color={
                  milestone.status === "ACHIEVED"
                    ? "var(--color-status-employ)"
                    : "var(--text-color-Subtittle)"
                }
              >
                {ui.amount ? formatAmount(ui.amount) : "-"}
              </Text>
            </Flex>
          );
        })}
      </AccordionPanel>
    </AccordionItem>
  );
};

const PopupReferralDetail = ({
  isOpen,
  onClose,
  data,
  filterLabel,
  scope,
  monthDate,
  year,
  referralSource,
  referredId,
}) => {
  const { referrals, isLoading, isRefetching } = useReferralEarningDetail(
    data?.code,
    scope,
    monthDate,
    year,
    referralSource,
    referredId,
  );

  // Duyệt nguồn: dùng lại popup + API duyệt đang có ở trang Quản lý nhân viên
  // (PUT /employee/updateStatusProfile/:id). Backend tự báo cho người giới thiệu.
  const queryClient = useQueryClient();
  const [dataApprove, setDataApprove] = useState(null);

  const handleOpenApprove = (item) =>
    setDataApprove({
      id: item?.id,
      code: item?.code,
      fullName: item?.fullName,
      mode: "update",
      title: "Duyệt nguồn giới thiệu",
      submitText: "Duyệt",
    });

  const onSubmitApprove = async ({ id, referralSource: source }) => {
    const toastUpdate = toast.loading("Đang duyệt ...");
    try {
      await employeeCall.updateStatusIsProfileCompleted(id, {
        isProfileCompleted: true,
        referralSource: source,
      });
      // Làm mới bảng, ô thống kê và danh sách trong popup
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["referralEarning"] }),
        queryClient.invalidateQueries({ queryKey: ["referralEarningDetail"] }),
      ]);
      setDataApprove(null);
      toast.success("Duyệt thành công");
    } catch (error) {
      console.log({ error });
    }
    toast.dismiss(toastUpdate);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        backgroundColor="var(--color-main)"
        borderRadius={"16px"}
        w={"100%"}
        maxW={"800px"}
      >
        <Flex
          justifyContent={"space-between"}
          alignItems={"center"}
          gap={"10px"}
          padding={"25px 24px"}
          borderBottom={"1px solid var(--bg-line-employee)"}
        >
          <Text
            color="var(--color-header-employee)"
            fontSize={"18px"}
            fontWeight={"600"}
          >
            Chi tiết thưởng giới thiệu
          </Text>
          <Box cursor={"pointer"} onClick={onClose}>
            <Image src={CloseIconBlack} />
          </Box>
        </Flex>
        <Flex p={"24px"} flexDirection={"column"} gap={"16px"}>
          <Box>
            <Text
              fontSize={"16px"}
              fontWeight={600}
              color="var(--color-header-employee)"
            >
              {data?.code} - {data?.name}
            </Text>
            <Text fontSize={"14px"} color="var(--text-color-Subtittle)">
              {filterLabel} · Số người giới thiệu: {data?.referredCount} · Tạm
              tính: {data?.provisionalAmount} · Thực nhận: {data?.paidAmount}
            </Text>
          </Box>
          <Box maxH={"60vh"} overflowY={"auto"}>
            <Skeleton isLoaded={!isLoading && !isRefetching}>
              {referrals.length > 0 ? (
                <Accordion allowMultiple>
                  {referrals.map((item, index) => (
                    <ReferredCard
                      key={item?.code || index}
                      item={item}
                      onApprove={handleOpenApprove}
                    />
                  ))}
                </Accordion>
              ) : (
                <Text
                  textAlign={"center"}
                  p={"40px 0"}
                  color="var(--text-color-Subtittle)"
                >
                  Chưa có nhân sự nào được giới thiệu.
                </Text>
              )}
            </Skeleton>
          </Box>
        </Flex>
      </ModalContent>
      {dataApprove && (
        <PopupProfileCompleted
          isOpen={!!dataApprove}
          onClose={() => setDataApprove(null)}
          onSubmit={onSubmitApprove}
          defaultValues={dataApprove}
        />
      )}
    </Modal>
  );
};

export default PopupReferralDetail;
